import { useEffect } from 'react';
import { useHilContext } from '@bizfirst/hil-react';

const HIL_RESOLVE_TYPES = new Set(['APPROVAL_RESOLVED', 'APPROVAL_ESCALATED']);

/**
 * Drives the HIL overlay from 'hil-event' window CustomEvents.
 *
 * Pushes raw v1.3 HilPresentationRequestEnvelope payloads directly to HilEngine via
 * pushToEngine() — no field mapping. HilEngine validates via HilEnvelopeValidator;
 * invalid payloads call onValidationError and are discarded.
 *
 * Resolution events (APPROVAL_RESOLVED, APPROVAL_ESCALATED) resolve the active session
 * in the engine via engine.resolve(engageSessionID).
 */
export function useHilSignalR(
  _connection: unknown,   // kept for API compatibility — managed by useExecutionSignalR
  _executionResId: string | null
): void {
  const { pushToEngine, engine } = useHilContext();

  useEffect(() => {
    const handleHilWindowEvent = (e: Event) => {
      const payload = (e as CustomEvent).detail;
      try {
        const hilData = payload?.dataEnvelope?.data ?? payload;
        const eventType: string = hilData?.eventType ?? '';

        if (HIL_RESOLVE_TYPES.has(eventType)) {
          const engageSessionID = Number(hilData.engageSessionId ?? hilData.EngageSessionID ?? 0);
          if (engageSessionID) engine.resolve(engageSessionID);
          return;
        }

        // Push raw v1.3 envelope — HilEngine validates and discards if invalid
        pushToEngine(hilData);
      } catch (err) {
        console.error('[useHilSignalR] Error handling HIL event:', err);
      }
    };

    window.addEventListener('hil-event', handleHilWindowEvent);
    return () => window.removeEventListener('hil-event', handleHilWindowEvent);
  }, [pushToEngine, engine]);
}
